/**
 * Pure decision logic for ProtectedRoute, extracted to allow unit testing without
 * mocking Clerk, Inversify, Jotai and react-query at the same time.
 *
 * Every authenticated user has at least one membership (UserTenant). The active
 * tenant of every API request is sourced from the `x-tenant-id` header, which is
 * injected by the ky-client interceptor from the `tenant_context` localStorage
 * entry. On first sign-in that entry is empty, so this helper decides whether to
 * auto-select the user's only tenant, or send them to the picker, or do nothing.
 *
 * `isBootstrappingAuth` gates on TanStack Query's `isPending`, not `isLoading`.
 * A query paused offline (fetchStatus 'paused') or disabled (no validated tenant
 * yet) reports `isLoading: false` but `isPending: true` with `data: undefined` —
 * treating that as "settled" would send a user with unresolved bootstrap data to
 * /onboarding. Tenant-scoped queries (onboarding/consent/permissions) are only
 * consulted once a tenant is validated, since a disabled query's `isPending` stays
 * true forever and would otherwise spin the loader indefinitely.
 */
interface MembershipSignal {
  tenantId: string;
}

interface ProtectedRouteDecisionInput {
  hasTenantContext: boolean;
  currentTenantId?: string | null;
  memberships: MembershipSignal[];
  /**
   * Flag User-level: il back office non è un consulente e non sceglie
   * aziende — con più membership (accessi di supporto) entra direttamente
   * nel primo tenant (il proprio, il più antico per createdAt).
   */
  isPlatformAdmin?: boolean;
}

type TenantSelectionDecision =
  | { kind: 'none' }
  | { kind: 'auto-select'; tenantId: string }
  | { kind: 'pick'; tenantIds: string[] };

interface PostOnboardingRedirectInput {
  onboardingCompleted: boolean;
  passwordEnabled: boolean;
  hasSsoAccount: boolean;
  trialExpired: boolean;
  needsTosOrConsent: boolean;
  /**
   * Il trial che scade è quello del tenant in cui capita di entrare: per il
   * back office è un tenant tecnico personale, non un abbonamento dovuto —
   * il gate /trial-expired non deve mai intrappolare l'accesso al back office.
   */
  isPlatformAdmin?: boolean;
}

interface BootstrapAuthInput {
  isLoaded: boolean;
  isSignedIn: boolean;
  membershipsPending: boolean;
  tenantDecisionKind: 'none' | 'auto-select' | 'pick';
  hasValidatedTenant: boolean;
  onboardingPending: boolean;
  consentPending: boolean;
  permissionsPending: boolean;
}

export function isBootstrappingAuth(input: BootstrapAuthInput): boolean {
  if (!input.isLoaded) {
    return true;
  }
  if (!input.isSignedIn) {
    return false;
  }
  if (input.membershipsPending) {
    return true;
  }
  if (input.tenantDecisionKind === 'auto-select') {
    return true;
  }
  return (
    input.hasValidatedTenant &&
    (input.onboardingPending || input.consentPending || input.permissionsPending)
  );
}

export function getPostOnboardingRedirect(input: PostOnboardingRedirectInput): string | null {
  if (!input.onboardingCompleted) {
    return '/onboarding';
  }
  if (!input.passwordEnabled && !input.hasSsoAccount) {
    return '/set-password';
  }
  if (input.trialExpired && !input.isPlatformAdmin) {
    return '/trial-expired';
  }
  if (input.needsTosOrConsent) {
    return '/accept-terms';
  }
  return null;
}

export function shouldTrackProvisioningGrace(
  membershipsPending: boolean,
  membershipsCount: number,
): boolean {
  return !membershipsPending && membershipsCount === 0;
}

/**
 * Zero memberships normally means "still provisioning". A consultant blocked on
 * every company also lands there (blocked memberships are omitted from the
 * list): in that case the dedicated "profile not active" screen replaces the
 * provisioning spinner / BootstrapError.
 */
export function shouldShowConsultantBlockedScreen(input: {
  membershipsPending: boolean;
  membershipsCount: number;
  consultantAccessBlocked: boolean;
}): boolean {
  return !input.membershipsPending && input.membershipsCount === 0 && input.consultantAccessBlocked;
}

export type MembershipsEmptyState = 'none' | 'spinner' | 'error';

export function resolveMembershipsEmptyState(input: {
  membershipsCount: number;
  provisioningGraceExpired: boolean;
}): MembershipsEmptyState {
  if (input.membershipsCount > 0) {
    return 'none';
  }
  return input.provisioningGraceExpired ? 'error' : 'spinner';
}

export function decideTenantSelection(input: ProtectedRouteDecisionInput): TenantSelectionDecision {
  const contextMatchesMembership =
    input.hasTenantContext &&
    !!input.currentTenantId &&
    input.memberships.some((m) => m.tenantId === input.currentTenantId);

  if (contextMatchesMembership) {
    return { kind: 'none' };
  }

  if (input.memberships.length === 0) {
    return { kind: 'none' };
  }

  const [first] = input.memberships;
  if (input.memberships.length === 1 && first) {
    return { kind: 'auto-select', tenantId: first.tenantId };
  }

  // Il back office non passa mai dal picker: entra nel proprio tenant (il
  // primo per ordine di creazione, quello di provisioning dell'admin-invite).
  if (first && input.isPlatformAdmin) {
    return { kind: 'auto-select', tenantId: first.tenantId };
  }

  return {
    kind: 'pick',
    tenantIds: input.memberships.map((m) => m.tenantId),
  };
}
